import * as Api from '@ViewModels';
import * as React from 'react';
import { SalesLeadAIContext } from '../../../extViewmodels/sdk/models/AI';
import { useStreamingResponseMutation } from '../../../hooks/useStreamingResponseMutation';
import { useErrorMessages, useUserSession } from '../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../models/Logging';
import { useAIAssistantCreateConversationMutation } from '../../../queries/AI/useAIAssistantCreateConversationMutation';
import { useAIConversationEnrichCreationContextMutation } from '../../../queries/AI/useAIConversationEnrichCreationContextMutation';
import { getConversationChatMessageQueryKey } from '../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { reactQueryClient } from '../../../web/ReactQueryProvider';
import {
	AICoachingMessage,
	TysonCoachingContextData,
	TYSON_ASSISTANT_NAME,
	TYSON_COACHING_FALLBACK_PROMPT,
	TYSON_END_OF_CALL_SUMMARY_PROMPT,
} from '../types/coaching';
import { TranscriptEntry } from '../types';

interface UseTysonRealtimeCoachingReturn {
	coachingMessages: AICoachingMessage[];
	conversationId: string | null;
	isCreatingConversation: boolean;
	isAIResponding: boolean;
	createConversation: (data?: TysonCoachingContextData) => Promise<void>;
	enrichCreationContext: (params: { phoneCallId?: string; leadServedId?: string; source?: string }) => void;
	sendTranscriptEntry: (entry: TranscriptEntry) => void;
	sendTranscriptBatchImmediately: (entries: TranscriptEntry[]) => void;
	flushPendingEntries: () => TranscriptEntry[];
	sendEndOfCallSummary: () => void;
	sendEndOfCallSummaryWithTranscript: (formattedTranscript: string) => void;
	sendDirectQuestion: (question: string) => void;
	sendSystemMessage: (message: string) => void;
	resetCoaching: () => void;
}

export const useTysonRealtimeCoaching = (contextData: TysonCoachingContextData): UseTysonRealtimeCoachingReturn => {
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('useTysonRealtimeCoaching');
	const userSession = useUserSession();

	const [conversationId, setConversationId] = React.useState<string>(null);
	const conversationIdRef = React.useRef<string>(null);
	const [coachingMessages, setCoachingMessages] = React.useState<AICoachingMessage[]>([]);
	// Pending entries are accumulated by sendTranscriptEntry and flushed by flushPendingEntries
	const [pendingTranscriptEntries, setPendingTranscriptEntries] = React.useState<TranscriptEntry[]>([]);

	// Keep ref in sync with state
	React.useEffect(() => {
		conversationIdRef.current = conversationId;
	}, [conversationId]);

	// Enrich creation context (e.g., add phoneCallId after a real call starts)
	const enrichCreationContextMutation = useAIConversationEnrichCreationContextMutation({
		onError: error => {
			logger.logApiError('enrichCreationContextMutation', error);
		},
	});

	// Create AI conversation
	const aiAssistantCreateConversationMutation = useAIAssistantCreateConversationMutation({
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiAssistantCreateConversationMutation', error);
		},
		onSuccess: conversation => {
			setConversationId(conversation.id);

			const coachingSystemPrompt = contextData?.objectivePrompt || TYSON_COACHING_FALLBACK_PROMPT;
			const currentDateTime = new Date().toLocaleString('en-US', {
				weekday: 'long',
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: 'numeric',
				minute: '2-digit',
				timeZoneName: 'short',
			});
			const systemPromptWithDate = `${coachingSystemPrompt}\n\nCurrent date and time: ${currentDateTime}`;

			const chatMessages: Api.AIChatMessage[] = [
				{
					role: Api.AIChatRole.System,
					text: systemPromptWithDate,
					id: null,
				},
				{
					role: Api.AIChatRole.User,
					text: `I am about to start calling contacts for this lead. Search their website and then based on everything you know, give me a quick Game Plan for these calls.

Output a short, glanceable Game Plan I can read in under 10 seconds. Simple bullets only — no headers, no sections, no paragraphs.

- 4–6 bullets max covering: what this business does, how they operate, what tools/systems they use, anything useful from their website, and contact personality or tone from prior calls
- If there's a critical flag from call history (hard objection, known to hang up fast, specific quote), include it as a bullet starting with ⚠️
- Never mention dial count, voicemail count, or anything that frames the lead as overdone
- No preamble, no sign-off, no section headers — just the bullets
- Be specific to THIS lead — no generic coaching language

After the bullets, end with: "Tell me who picks up and I'll coach you through it live."

I will let you know each time I start a new call with a specific contact so you can give me live coaching.`,
					id: null,
				},
			];

			// Start streaming the initial AI response, including lead context if companyId is available
			const contexts: Api.AIContext[] = [];
			if (contextData?.companyId) {
				contexts.push({
					_type: 'SalesLeadAIContext',
					companyId: contextData.companyId,
				} as SalesLeadAIContext);
			}

			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: conversation.id,
				chatRequest: {
					...(contexts.length > 0 && { contexts }),
					chatMessages,
				},
			});
		},
	});

	// Stream AI responses
	const aiPromptMutation = useStreamingResponseMutation({
		mutationFn: ({
			assistantName,
			convoId,
			chatRequest,
		}: {
			assistantName: string;
			convoId: string;
			chatRequest: Api.AIMessageRequest;
		}) =>
			new Promise<Response>((resolve, reject) => {
				userSession.webServiceHelper.callWebServiceRawResponse(
					Api.ImpersonationBroker.composeApiUrl({
						urlPath: `ai/assistant/${assistantName}/conversation/${convoId}/stream`,
					}),
					'POST',
					chatRequest,
					resolve,
					reject
				);
			}),
		onEvent: (event, params) => {
			const data = JSON.parse(event.data);
			if (event.event !== 'Response') {
				return;
			}

			const messageText = data?.message?.text;
			const isDelta = data?.isDelta;

			if (messageText) {
				setCoachingMessages(prev => {
					const lastMessage = prev[prev.length - 1];

					if (lastMessage && lastMessage.isStreaming) {
						// Update existing streaming message - just concatenate directly
						// LLM tokens include proper spacing
						const updatedText = isDelta ? lastMessage.text + messageText : messageText;

						return [
							...prev.slice(0, -1),
							{
								...lastMessage,
								text: updatedText,
							},
						];
					} else {
						// Create new message
						return [
							...prev,
							{
								id: data.Value?.Id || `msg-${Date.now()}`,
								text: messageText,
								timestamp: new Date(),
								isStreaming: true,
								role: 'assistant',
							},
						];
					}
				});

				// Update query cache
				reactQueryClient.setQueryData(
					getConversationChatMessageQueryKey({ id: params.convoId }),
					(messages: Api.AIChatMessage[]) => {
						if (messages?.length > 0 && messages[messages.length - 1].role === Api.AIChatRole.Assistant) {
							const lastAssistantMessage = messages.pop();
							// LLM tokens include proper spacing - just concatenate directly
							const updatedText = isDelta ? lastAssistantMessage.text + messageText : messageText;
							return [...messages, { ...lastAssistantMessage, text: updatedText }];
						}
						return [...(messages || []), { id: data.Value?.Id, text: messageText, role: Api.AIChatRole.Assistant }];
					}
				);
			}
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError('aiPrompt', error as Api.IOperationResultNoValue);
		},
		onSuccess: () => {
			// Mark last message as complete
			setCoachingMessages(prev => {
				if (prev.length === 0) return prev;
				const lastMessage = prev[prev.length - 1];
				return [...prev.slice(0, -1), { ...lastMessage, isStreaming: false }];
			});
		},
	});

	// Send transcript batch immediately - used by "Get Suggestion" button
	const sendTranscriptBatchImmediately = React.useCallback(
		(entries: TranscriptEntry[]) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId || entries.length === 0) {
				return;
			}

			// Clear any pending entries to avoid duplicates
			setPendingTranscriptEntries([]);

			const formattedTranscript = entries
				.map(entry => `[${entry.timestamp.toLocaleTimeString()}] ${entry.speaker}: ${entry.message}`)
				.join('\n');

			const transcriptText = `New transcript:\n${formattedTranscript}`;

			// Add user message showing what was sent
			const transcriptMessage: AICoachingMessage = {
				id: `transcript-${Date.now()}`,
				text: transcriptText,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'transcript',
			};

			setCoachingMessages(prev => [...prev, transcriptMessage]);

			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: transcriptText,
							id: null,
						},
					],
				},
			});
		},
		[aiPromptMutation]
	);

	// Send single transcript entry - accumulates entries until manually sent
	const sendTranscriptEntry = React.useCallback(
		(entry: TranscriptEntry) => {
			if (!conversationIdRef.current) {
				return;
			}
			setPendingTranscriptEntries(prev => [...prev, entry]);
		},
		[] // No dependencies - always use latest ref value
	);

	// Flush pending entries and return them (for manual "Get Suggestion")
	const flushPendingEntries = React.useCallback((): TranscriptEntry[] => {
		const entries = [...pendingTranscriptEntries];
		setPendingTranscriptEntries([]);
		return entries;
	}, [pendingTranscriptEntries]);

	// Create conversation
	const createConversation = React.useCallback(
		async (data?: TysonCoachingContextData) => {
			if (aiAssistantCreateConversationMutation.isPending) return;

			// Use provided data or fall back to contextData
			const ctx = data || contextData;

			// Validate context data
			if (!ctx) {
				throw new Error('Cannot create conversation: contextData is null');
			}

			if (!ctx.companyId) {
				throw new Error('Cannot create conversation: companyId is required');
			}

			if (!ctx.contactId) {
				throw new Error('Cannot create conversation: contactId is required');
			}

			// Create conversation
			await aiAssistantCreateConversationMutation.mutateAsync({
				assistantName: TYSON_ASSISTANT_NAME,
				phoneCallId: ctx.phoneCallId,
				companyId: ctx.companyId,
				leadServedId: ctx.leadServedId,
			});
		},
		[contextData, aiAssistantCreateConversationMutation]
	);

	// Send end-of-call summary request
	const sendEndOfCallSummary = React.useCallback(() => {
		const currentConversationId = conversationIdRef.current;
		if (!currentConversationId) {
			return;
		}

		aiPromptMutation.mutate({
			assistantName: TYSON_ASSISTANT_NAME,
			convoId: currentConversationId,
			chatRequest: {
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: TYSON_END_OF_CALL_SUMMARY_PROMPT,
						id: null,
					},
				],
			},
		});
	}, [aiPromptMutation]);

	// Send end-of-call summary with transcript included in one request
	const sendEndOfCallSummaryWithTranscript = React.useCallback(
		(formattedTranscript: string) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId) {
				return;
			}

			// Add transcript message to UI
			const combinedText = `New transcript:\n${formattedTranscript}\n\n---\n\n${TYSON_END_OF_CALL_SUMMARY_PROMPT}`;

			const transcriptMessage: AICoachingMessage = {
				id: `transcript-${Date.now()}`,
				text: combinedText,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'transcript',
			};
			setCoachingMessages(prev => [...prev, transcriptMessage]);

			// Send transcript + summary prompt in a single user message
			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: combinedText,
							id: null,
						},
					],
				},
			});
		},
		[aiPromptMutation]
	);

	// Send a direct question from the user (not from call transcript)
	const sendDirectQuestion = React.useCallback(
		(question: string) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId || !question.trim()) return;

			// Add user message to conversation
			const userMessage: AICoachingMessage = {
				id: `question-${Date.now()}`,
				text: question,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'question',
			};
			setCoachingMessages(prev => [...prev, userMessage]);

			// Send with wrapper context
			const wrappedQuestion = `The sales rep is asking you a direct question (not from the call transcript):\n${question}`;

			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: wrappedQuestion,
							id: null,
						},
					],
				},
			});
		},
		[aiPromptMutation]
	);

	// Send a system notification message (no wrapper text, shown as system message in UI)
	const sendSystemMessage = React.useCallback(
		(message: string) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId || !message.trim()) return;

			// Add to coaching messages with system messageType
			const systemMsg: AICoachingMessage = {
				id: `system-${Date.now()}`,
				text: message,
				timestamp: new Date(),
				isStreaming: false,
				role: 'user',
				messageType: 'system',
			};
			setCoachingMessages(prev => [...prev, systemMsg]);

			// Send to AI as a user message (no wrapper)
			aiPromptMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				convoId: currentConversationId,
				chatRequest: {
					chatMessages: [
						{
							role: Api.AIChatRole.User,
							text: message,
							id: null,
						},
					],
				},
			});
		},
		[aiPromptMutation]
	);

	// Enrich the creation context of an existing conversation (e.g., add phoneCallId after Twilio call starts)
	const enrichCreationContext = React.useCallback(
		(params: { phoneCallId?: string; leadServedId?: string; source?: string }) => {
			const currentConversationId = conversationIdRef.current;
			if (!currentConversationId) return;

			// Only call the API if there's at least one field to enrich
			if (!params.phoneCallId && !params.leadServedId && !params.source) return;

			enrichCreationContextMutation.mutate({
				assistantName: TYSON_ASSISTANT_NAME,
				conversationId: currentConversationId,
				...params,
			});
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[]
	);

	// Reset coaching state
	const resetCoaching = React.useCallback(() => {
		setConversationId(null);
		conversationIdRef.current = null;
		setCoachingMessages([]);
		setPendingTranscriptEntries([]);
	}, []);

	return {
		coachingMessages,
		conversationId,
		isCreatingConversation: aiAssistantCreateConversationMutation.isPending,
		isAIResponding: aiPromptMutation.isStreaming || aiPromptMutation.isLoading,
		createConversation,
		enrichCreationContext,
		sendTranscriptEntry,
		sendTranscriptBatchImmediately,
		flushPendingEntries,
		sendEndOfCallSummary,
		sendEndOfCallSummaryWithTranscript,
		sendDirectQuestion,
		sendSystemMessage,
		resetCoaching,
	};
};
